import React, { memo, useMemo } from 'react';

import DatePicker from 'react-datepicker';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faCircleInfo, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import 'react-datepicker/dist/react-datepicker.css';

import i18n from '../messages.json';
import PreviewView from './PreviewView';
import TimeSelect from './TimeSelect';
import { nowEasternWall, easternAbbrev } from '../../../utils/schedule-helper';

/**
 * "Send Later" scheduling popup (WWW-braze-update). Opened from the Review-and-send
 * footer's Send Later button — the Review modal hides while this is open. Shows a
 * recap of what's being sent (the same PreviewView: device cards, article/Direct
 * URL, segment pills) beside a clean Date (inline calendar) + Time (dropdown)
 * picker. The picked wall-clock is always Eastern (the newsroom zone), echoed back
 * as a "Delivers" summary. NotificationForm converts it to an absolute instant and
 * schedules the Braze send.
 */
const ScheduleDialog = ({
  show,
  scheduleTime,
  onScheduleTimeChange,
  onCancel,
  onConfirm,
  confirmDisabled,
  isScheduling,
  preview,
}) => {
  const labels = i18n.ink.notification.scheduled;

  const minDate = useMemo(() => nowEasternWall(), []);

  const tzAbbrev = useMemo(
    () => easternAbbrev(scheduleTime ? new Date(scheduleTime) : new Date()),
    [scheduleTime],
  );

  // "Aug 15, 2026 · 2:30 PM EDT" — scheduleTime's local fields already hold the
  // Eastern wall-clock, so plain local formatting reads back the intended time.
  const summary = useMemo(() => {
    if (!scheduleTime) return null;

    const d = new Date(scheduleTime);
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    return `${date} · ${time} ${tzAbbrev}`;
  }, [scheduleTime, tzAbbrev]);

  return (
    <Modal
      show={show}
      onHide={onCancel}
      centered
      backdrop='static'
      dialogClassName='schedule-dialog modern-modal'
      backdropClassName='notification-compose-backdrop'
    >
      <Modal.Header closeButton>
        <div className='schedule-dialog__heading'>
          <span className='schedule-dialog__heading-icon'>
            <FontAwesomeIcon icon={faCalendarDay} />
          </span>
          <div>
            <Modal.Title className='schedule-dialog__title'>{labels.dialogTitle}</Modal.Title>
            <div className='schedule-dialog__subtitle'>{labels.dialogSubtitle}</div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className='schedule-dialog__body'>
        <div className='schedule-dialog__grid'>
          <div className='schedule-dialog__recap'>
            {preview && <PreviewView {...preview} />}

            {/* Delivery-channel heads-up, tied to the audience it affects. */}
            <div className='schedule-dialog__callout schedule-dialog__callout--warning'>
              <FontAwesomeIcon icon={faTriangleExclamation} className='schedule-dialog__callout-icon' />
              <span>{labels.brazeOnlyNote}</span>
            </div>
          </div>

          <div className='schedule-dialog__when'>
            <div className='schedule-dialog__summary'>
              <span className='schedule-dialog__summary-label'>{labels.deliversLabel}</span>
              <span className={`schedule-dialog__summary-value${summary ? '' : ' schedule-dialog__summary-value--empty'}`}>
                {summary || labels.deliversPlaceholder}
              </span>
            </div>

            <div className='schedule-dialog__field'>
              <span className='schedule-dialog__field-label'>{labels.dateLabel}</span>
              <div className='schedule-dialog__calendar'>
                <DatePicker inline selected={scheduleTime} onChange={onScheduleTimeChange} minDate={minDate} />
              </div>
            </div>

            <div className='schedule-dialog__field schedule-dialog__field--time'>
              <span className='schedule-dialog__field-label'>
                {labels.timeLabel} <span className='schedule-dialog__tz'>({tzAbbrev})</span>
              </span>
              {/* Type a time or pick from the dropdown; past times hidden for today. */}
              <TimeSelect
                value={scheduleTime}
                minDate={minDate}
                onChange={onScheduleTimeChange}
                placeholder={labels.timePlaceholder}
              />
            </div>

            <div className='schedule-dialog__callout'>
              <FontAwesomeIcon icon={faCircleInfo} className='schedule-dialog__callout-icon' />
              <span>{labels.easternNote}</span>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='light' disabled={isScheduling} onClick={onCancel}>
          {i18n.ink.notification.buttons.cancel}
        </Button>
        <Button variant='primary' disabled={confirmDisabled} onClick={onConfirm}>
          {isScheduling ? labels.scheduling : i18n.ink.notification.buttons.schedule}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(ScheduleDialog);
